import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { formatDZ } from '../../../utils/currency';

const ProductCard = ({ product, onAddToCart, onAddToWishlist, onCompare, isInWishlist, isInComparison }) => {
  const [isHovered, setIsHovered] = useState(false);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={14} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={14} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={14} className="text-muted-foreground" />
      );
    }

    return stars;
  };

  const getAvailabilityStatus = () => {
    switch (product?.availability) {
      case 'in-stock':
        return { text: 'In Stock', color: 'text-success', icon: 'Check' };
      case 'low-stock':
        return { text: 'Low Stock', color: 'text-warning', icon: 'AlertTriangle' };
      case 'pre-order':
        return { text: 'Pre-Order', color: 'text-primary', icon: 'Clock' };
      case 'out-of-stock':
        return { text: 'Out of Stock', color: 'text-error', icon: 'X' };
      default:
        return { text: 'Available', color: 'text-success', icon: 'Check' };
    }
  };

  const availability = getAvailabilityStatus();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="holographic-card rounded-lg overflow-hidden group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Product Image */}
      <div className="relative overflow-hidden bg-muted/20">
        <Image
          src={product?.image}
          alt={product?.name}
          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
        />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col space-y-2">
          {product?.isNew && (
            <span className="bg-primary text-primary-foreground text-xs font-semibold px-2 py-1 rounded-full">
              NEW
            </span>
          )}
          {product?.discount && (
            <span className="bg-error text-error-foreground text-xs font-semibold px-2 py-1 rounded-full">
              -{product?.discount}%
            </span>
          )}
          {product?.isBestSeller && (
            <span className="bg-warning text-warning-foreground text-xs font-semibold px-2 py-1 rounded-full">
              BESTSELLER
            </span>
          )}
        </div>

        {/* Quick Actions */}
        <div className={`absolute top-3 right-3 flex flex-col space-y-2 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <Button
            variant={isInWishlist ? "default" : "outline"}
            size="icon"
            onClick={() => onAddToWishlist(product?.id)}
            className="bg-background/80 backdrop-blur-sm"
          >
            <Icon 
              name="Heart" 
              size={16} 
              className={isInWishlist ? "fill-current" : ""} 
            />
          </Button>
          <Button
            variant={isInComparison ? "default" : "outline"}
            size="icon"
            onClick={() => onCompare(product?.id)}
            className="bg-background/80 backdrop-blur-sm"
          >
            <Icon 
              name="GitCompare" 
              size={16} 
              className={isInComparison ? "text-primary" : ""} 
            />
          </Button>
        </div>

        {/* Availability Indicator */}
        <div className="absolute bottom-3 left-3">
          <div className={`flex items-center space-x-1 bg-background/80 backdrop-blur-sm rounded-full px-2 py-1 ${availability?.color}`}>
            <Icon name={availability?.icon} size={12} />
            <span className="text-xs font-medium">{availability?.text}</span>
          </div>
        </div>
      </div>
      {/* Product Info */}
      <div className="p-4">
        {/* Brand & Category */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-primary font-medium uppercase tracking-wide">
            {product?.brand}
          </span>
          <span className="text-xs text-muted-foreground">
            {product?.category}
          </span>
        </div>

        {/* Product Name */}
        <h3 className="font-semibold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-200">
          {product?.name}
        </h3>

        {/* Rating & Reviews */}
        <div className="flex items-center space-x-2 mb-3">
          <div className="flex items-center space-x-1">
            {renderStars(product?.rating)}
          </div>
          <span className="text-sm text-muted-foreground">
            ({product?.reviewCount})
          </span>
        </div>

        {/* Key Features */}
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {product?.keyFeatures?.slice(0, 2)?.map((feature, index) => (
              <span
                key={index}
                className="text-xs bg-muted/50 text-muted-foreground px-2 py-1 rounded-full"
              >
                {feature}
              </span>
            ))}
          </div>
        </div>

        {/* Price */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-foreground">
              {formatDZ(product?.price)}
            </span>
            {product?.originalPrice && (
              <span className="text-sm text-muted-foreground line-through">
                {formatDZ(product?.originalPrice)}
              </span>
            )}
          </div>
          {product?.discount && (
            <span className="text-sm text-success font-medium">
              {`Save ${formatDZ(product?.originalPrice - product?.price)}`}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          <Button
            variant="default"
            fullWidth
            onClick={() => onAddToCart(product?.id)}
            disabled={product?.availability === 'out-of-stock'}
            iconName="ShoppingCart"
            iconPosition="left"
            className="flex-1"
          >
            {product?.availability === 'out-of-stock' ? 'Out of Stock' : 
             product?.availability === 'pre-order' ? 'Pre-Order' : 'Add to Cart'}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => {/* Navigate to product detail */}}
          >
            <Icon name="Eye" size={16} />
          </Button>
        </div>

        {/* Shipping Info */}
        {product?.freeShipping && (
          <div className="flex items-center justify-center space-x-1 mt-3 text-xs text-success">
            <Icon name="Truck" size={12} />
            <span>Free Shipping</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;